import React from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    Chip,
    Grid,
    Paper,
    Avatar,
    Stack,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { handleLeave } from 'leaveManagement/redux.actions';
import { getFileURL } from 'common/redux.actions';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

const propTypes = {
    open: PropTypes.bool,
    payload: PropTypes.object,
    handleClose: PropTypes.func,
    onActionSuccess: PropTypes.func,
};

// Modern Dialog Styles - matches admin AppliedLeaves exactly
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 4,
        minWidth: 600,
        maxWidth: 700,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
            maxWidth: '95vw',
            margin: '16px',
            width: 'calc(100vw - 32px)'
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: '24px 32px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px'
    },
    '& .MuiTypography-root': {
        fontWeight: 600,
        fontSize: '1.25rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.1rem'
        }
    }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    padding: '16px 20px',
    marginBottom: '12px',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
        padding: '12px 16px'
    }
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    '& .icon': {
        color: theme.palette.primary.main,
        fontSize: '20px'
    }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const getStatusColor = () => {
        switch (status) {
            case 'approved':
                return {
                    bg: 'transparent',
                    color: theme.palette.success.main,
                    border: theme.palette.success.main
                };
            case 'rejected':
                return {
                    bg: 'transparent',
                    color: theme.palette.error.main,
                    border: theme.palette.error.main
                };
            case 'pending':
                return {
                    bg: 'transparent',
                    color: theme.palette.warning.main,
                    border: theme.palette.warning.main
                };
            default:
                return {
                    bg: 'transparent',
                    color: theme.palette.grey[700],
                    border: theme.palette.grey[400]
                };
        }
    };

    const colors = getStatusColor();

    return {
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'capitalize',
        padding: '4px 8px',
        height: '32px'
    };
});

const LeaveRequestDetails = ({ open, payload, handleClose, onActionSuccess }) => {
    const RXDispatch = useDispatch();

    if (!payload) return null;

    const handleLinkClick = async (e, docUrl) => {
        e.preventDefault();
        RXDispatch(getFileURL({
            handleSubmit: (resp) => {
                if (resp.type === "success") {
                    window.open(resp.url, "_blank");
                }
            },
            payload: { url: docUrl }
        }));
    };

    const handleApprove = () => {
        RXDispatch(handleLeave({ payload: { row: payload, action: 'approve' } }));
        handleClose();
        setTimeout(() => {
            if (onActionSuccess) onActionSuccess();
        }, 500);
    };

    const handleReject = () => {
        RXDispatch(handleLeave({ payload: { row: payload, action: 'reject' } }));
        handleClose();
        setTimeout(() => {
            if (onActionSuccess) onActionSuccess();
        }, 500);
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <StyledDialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <EventIcon sx={(theme) => ({ fontSize: 28, color: theme.palette.primary.main })} />
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                            Leave Request Details
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={handleClose}
                        sx={(theme) => ({
                            color: theme.palette.text.primary,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover
                            }
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </StyledDialogTitle>

            <DialogContent sx={(theme) => ({
                padding: '32px',
                paddingTop: '28px !important',
                backgroundColor: theme.palette.background.paper,
                '@media (max-width: 600px)': {
                    padding: '20px 16px',
                    paddingTop: '20px !important'
                }
            })}>
                {/* Status Badge */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip
                            label={payload?.status || 'Pending'}
                            status={payload?.status}
                        />
                        {payload?.isEmergency && (
                            <Chip
                                label="Emergency"
                                size="small"
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: (theme) => theme.palette.error.main,
                                    fontWeight: 600,
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                    height: '32px'
                                }}
                            />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 16 }} />
                            Applied: {payload?.createdAt ? dayjs(payload.createdAt).format('MMM DD, YYYY') : 'N/A'}
                        </Typography>
                        {payload?.updatedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 16 }} />
                                Updated: {dayjs(payload.updatedAt).format('MMM DD, YYYY')}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* Employee Information */}
                <InfoCard elevation={0}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Employee Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InfoRow>
                                <Avatar sx={(theme) => ({ bgcolor: theme.palette.primary.main, width: 40, height: 40 })}>
                                    {payload?.employee?.initial?.[0] || <PersonIcon />}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Full Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                        {payload?.employee?.initial ? `${payload.employee.initial} ` : ''}
                                        {payload?.employee?.firstname || payload?.employee?.middlename || payload?.employee?.lastname
                                            ? `${payload?.employee?.firstname || ''} ${payload?.employee?.middlename || ''} ${payload?.employee?.lastname || ''}`.trim()
                                            : 'N/A'
                                        }
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <EmailIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Email
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {payload?.employeeEmail || payload?.employee?.userId || 'N/A'}
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <BadgeIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Employee ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {payload?.employeeId || payload?.employee?.employeeId || 'N/A'}
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        {payload?.employee?.gender && (
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <PersonIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Gender
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {payload.employee.gender}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                        )}
                        {payload?.employee?.reportingManager && (
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <PersonIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Reporting Manager
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {payload.employee.reportingManager}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                        )}
                        {payload?.updatedBy && (
                            <Grid item xs={12}>
                                <InfoRow>
                                    <PersonIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Updated By
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {payload.updatedBy}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Leave Information */}
                <InfoCard elevation={0}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Leave Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InfoRow>
                                <CategoryIcon className="icon" />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Leave Type
                                    </Typography>
                                    <Typography variant="body2" sx={(theme) => ({ fontWeight: 600, mt: 0.5, color: theme.palette.primary.main })}>
                                        {payload?.categoryName || payload?.category?.name || 'N/A'}
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <CalendarTodayIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Start Date
                                    </Typography>
                                    <Typography variant="body2" sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.main })}>
                                        {payload?.startDate ? dayjs(payload.startDate).format('MMM DD, YYYY (ddd)') : 'N/A'}
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <CalendarTodayIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        End Date
                                    </Typography>
                                    <Typography variant="body2" sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.main })}>
                                        {payload?.endDate ? dayjs(payload.endDate).format('MMM DD, YYYY (ddd)') : 'N/A'}
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <AccessTimeIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Total Days
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {payload?.totalNumberOfDays || payload?.duration
                                            ? `${payload?.totalNumberOfDays || payload?.duration} ${(payload?.totalNumberOfDays || payload?.duration) === 1 ? 'day' : 'days'}`
                                            : 'N/A'
                                        }
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow>
                                <AccessTimeIcon className="icon" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Day Type
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                        {(payload?.totalNumberOfDays === 1 || payload?.duration === 1)
                                            ? 'Full Day'
                                            : (payload?.totalNumberOfDays === 0.5 || payload?.duration === 0.5)
                                            ? 'Half Day'
                                            : (payload?.totalNumberOfDays === 0.25 || payload?.duration === 0.25)
                                            ? 'Quarter Day'
                                            : payload?.isHalfDay
                                            ? 'Half Day'
                                            : 'Full Day'
                                        }
                                    </Typography>
                                </Box>
                            </InfoRow>
                        </Grid>
                        {payload?.category?.totalPerBusinessYear && (
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <EventIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Total Allowed Per Year
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {payload.category.totalPerBusinessYear} days
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Reason/Description */}
                <InfoCard elevation={0}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <DescriptionIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        Reason for Leave
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                        {payload?.reason || 'No reason provided'}
                    </Typography>
                </InfoCard>

                {/* Comment Section */}
                {payload?.comment && (
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            💬 Comment
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                            {payload.comment}
                        </Typography>
                    </InfoCard>
                )}

                {/* Documents */}
                <InfoCard elevation={0}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <AttachFileIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        Attachments {payload?.requiredDocuments && payload.requiredDocuments.length > 0 ? `(${payload.requiredDocuments.length})` : ''}
                    </Typography>
                    {payload?.requiredDocuments && payload.requiredDocuments.length > 0 ? (
                        <Stack spacing={1}>
                            {payload.requiredDocuments.map((docUrl, docIndex) => (
                                <Box
                                    key={docIndex}
                                    sx={(theme) => ({
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        padding: '8px 12px',
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`
                                    })}
                                >
                                    <AttachFileIcon sx={(theme) => ({ fontSize: 18, color: theme.palette.primary.main })} />
                                    <a
                                        href={docUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: 'inherit',
                                            textDecoration: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            flex: 1
                                        }}
                                        onClick={(e) => handleLinkClick(e, docUrl)}
                                    >
                                        Document {docIndex + 1}
                                    </a>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No documents attached
                        </Typography>
                    )}
                </InfoCard>

                {/* Employees to Notify Section */}
                {payload?.employeesToNotify && payload.employeesToNotify.length > 0 && (
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <EmailIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                            Employees to Notify
                        </Typography>
                        <Stack spacing={1.5}>
                            {payload.employeesToNotify.map((employee, index) => (
                                <Box
                                    key={index}
                                    sx={(theme) => ({
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        padding: '10px 12px',
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`
                                    })}
                                >
                                    <Avatar sx={(theme) => ({ bgcolor: theme.palette.primary.main, width: 36, height: 36 })}>
                                        {(employee?.firstname?.[0] || '') + (employee?.lastname?.[0] || '')}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {employee?.initial ? `${employee.initial} ` : ''}
                                            {`${employee?.firstname || ''} ${employee?.middlename || ''} ${employee?.lastname || ''}`.trim()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {employee?.userId || 'N/A'} • {employee?.employeeId || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </InfoCard>
                )}

                {/* Rejection Reason */}
                {payload?.rejectReason && (
                    <InfoCard elevation={0} sx={(theme) => ({ backgroundColor: theme.palette.error.alert, borderColor: theme.palette.error.light })}>
                        <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            ⚠️ Rejection Reason
                        </Typography>
                        <Typography variant="body2" color="error.dark" sx={{ lineHeight: 1.7 }}>
                            {payload.rejectReason}
                        </Typography>
                    </InfoCard>
                )}
            </DialogContent>

            {/* Footer with approve/reject actions */}
            <DialogActions sx={(theme) => ({
                padding: '20px 32px',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 1,
                flexDirection: 'column',
                alignItems: 'stretch',
                '@media (max-width: 600px)': {
                    padding: '16px 20px'
                }
            })}>
                {/* Info message for non-pending status */}
                {payload?.status === 'approved' && (
                    <Alert severity="success" sx={{ mb: 1 }}>
                        This leave is already <strong>approved</strong>{payload?.updatedBy ? ` by ${payload.updatedBy}` : ''}, but you can reject it if needed.
                    </Alert>
                )}
                {payload?.status === 'rejected' && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        This leave is already <strong>rejected</strong>{payload?.updatedBy ? ` by ${payload.updatedBy}` : ''}, but you can approve it if needed.
                    </Alert>
                )}

                {/* Action buttons */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    '@media (max-width: 600px)': {
                        flexDirection: 'column',
                        '& button': {
                            width: '100%'
                        }
                    }
                }}>
                    {payload?.status === 'pending' && (
                        <>
                            <Button
                                onClick={handleReject}
                                variant="outlined"
                                color="error"
                                sx={{ minWidth: 100 }}
                            >
                                Reject
                            </Button>
                            <Button
                                onClick={handleApprove}
                                variant="contained"
                                color="success"
                                sx={{ minWidth: 100, color: '#fff' }}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                    {payload?.status === 'approved' && (
                        <Button
                            onClick={handleReject}
                            variant="outlined"
                            color="error"
                            sx={{ minWidth: 100 }}
                        >
                            Reject
                        </Button>
                    )}
                    {payload?.status === 'rejected' && (
                        <Button
                            onClick={handleApprove}
                            variant="contained"
                            color="success"
                            sx={{
                                minWidth: 100,
                                color: '#fff',
                            }}
                        >
                            Approve
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </StyledDialog>
    );
};

LeaveRequestDetails.propTypes = propTypes;

export default LeaveRequestDetails;
